/**
 * Recents in Home's sidebar: the organization's whole chat history,
 * T3 Code's thread rows on Home. Pinned and active chats first, paged in with
 * "Show more"; then the Snoozed and Settled shelves. A filter shows every
 * match in every section, nothing paged or folded.
 *
 * @module components/home/HomeSidebarRecents.logic
 */
import { scopeThreadRef, scopedThreadKey } from "@amoreg/client-runtime/environment";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import type { KeybindingCommand, ResolvedKeybindingsConfig } from "@amoreg/contracts";

import {
  firstValidTimestampMs,
  resolveAdjacentThreadId,
  sortPinnedThreadsForSidebar,
  sortSettledThreadsForSidebar,
  type SidebarThreadStatus,
} from "../Sidebar.logic";
import {
  resolveShortcutCommand,
  type ShortcutEventLike,
  threadJumpIndexFromCommand,
  threadTraversalDirectionFromCommand,
} from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";
import { isModelPickerOpen } from "../../modelPickerVisibility";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { threadActivityMs } from "./homeLogic";

/** Rows before the first "Show more". */
export const RECENTS_FIRST_PAGE = 10;
/** Rows each "Show more" adds, to Recents and to Settled. */
export const RECENTS_PAGE_COUNT = 25;
/** Settled rows shown before its "Show more". */
export const SETTLED_PREVIEW_COUNT = 3;
/** Active rows always shown, however many chats are pinned. */
const MIN_ACTIVE_ROWS = 3;

export type RecentsSection = "pinned" | "active" | "snoozed" | "settled";

export interface RecentsRow {
  readonly thread: EnvironmentThreadShell;
  readonly section: RecentsSection;
}

/**
 * One item of Recents' tree: a chat (`index` is its place in `rows`), a
 * shelf header, or "Show N more" under the active chats or Settled.
 */
export type RecentsItem =
  | { readonly kind: "row"; readonly row: RecentsRow; readonly index: number }
  | { readonly kind: "shelf"; readonly shelf: "snoozed" | "settled"; readonly count: number; readonly open: boolean }
  | { readonly kind: "more"; readonly under: "active" | "settled"; readonly count: number };

export interface RecentsModel {
  /** Every visible row, top to bottom. */
  readonly rows: ReadonlyArray<RecentsRow>;
  /** Every tree item, rows, shelf headers and "Show more", top to bottom. */
  readonly items: ReadonlyArray<RecentsItem>;
  readonly snoozedCount: number;
  readonly settledCount: number;
  /** Settled rows under Settled's "Show more". */
  readonly settledHidden: number;
  /** Active rows under Recents' "Show more". */
  readonly activeHidden: number;
  /** A filter is on: every section shows all its matches. */
  readonly filtering: boolean;
}

export interface RecentsInput {
  /** The organization's chats. */
  readonly threads: ReadonlyArray<EnvironmentThreadShell>;
  /** The shelf a chat rests on, or null while it is pinned or active. */
  readonly shelfOf: (thread: EnvironmentThreadShell) => "snoozed" | "settled" | null;
  /** Pinned plus active rows to show before "Show more". */
  readonly limit: number;
  readonly settledVisible: number;
  readonly snoozedOpen: boolean;
  readonly settledOpen: boolean;
  /** The open chat: it never hides under "Show more", it joins the visible tail. */
  readonly routeThreadId: string | null;
  readonly query: string;
}

const foldCase = (value: string) => value.toLocaleLowerCase();

/** Where `query` first matches `text`, ignoring case; null for no match or an empty query. */
export function matchRange(text: string, query: string): { start: number; end: number } | null {
  const needle = foldCase(query.trim());
  if (needle === "") return null;
  const start = foldCase(text).indexOf(needle);
  return start === -1 ? null : { start, end: start + needle.length };
}

export function buildRecentsModel(input: RecentsInput): RecentsModel {
  const filtering = input.query.trim() !== "";
  const settledVisible = filtering ? Number.POSITIVE_INFINITY : input.settledVisible;
  const snoozedOpen = filtering || input.snoozedOpen;
  const settledOpen = filtering || input.settledOpen;
  const pinned: EnvironmentThreadShell[] = [];
  const active: EnvironmentThreadShell[] = [];
  const snoozed: EnvironmentThreadShell[] = [];
  const settled: EnvironmentThreadShell[] = [];
  for (const thread of input.threads) {
    if (filtering && matchRange(thread.title, input.query) === null) continue;
    const shelf = input.shelfOf(thread);
    if (shelf === "snoozed") snoozed.push(thread);
    else if (shelf === "settled") settled.push(thread);
    else if (thread.pinnedAt != null) pinned.push(thread);
    else active.push(thread);
  }
  const pinnedSorted = sortPinnedThreadsForSidebar(pinned);
  const activeAll = active.toSorted((left, right) => threadActivityMs(right) - threadActivityMs(left));
  // Pins share the first page; every "Show more" after it adds whole pages of active chats, however many pins there are.
  const activeCap = filtering
    ? Number.POSITIVE_INFINITY
    : Math.max(MIN_ACTIVE_ROWS, RECENTS_FIRST_PAGE - pinnedSorted.length) + Math.max(0, input.limit - RECENTS_FIRST_PAGE);
  const withRouteThread = (all: EnvironmentThreadShell[], cap: number) => {
    const visible = all.slice(0, cap);
    const routeThread =
      input.routeThreadId === null ? undefined : all.slice(cap).find((thread) => thread.id === input.routeThreadId);
    return routeThread ? [...visible, routeThread] : visible;
  };
  const snoozedSorted = snoozed.toSorted(
    (left, right) =>
      firstValidTimestampMs(left.snoozedUntil ?? null) - firstValidTimestampMs(right.snoozedUntil ?? null),
  );
  const settledSorted = sortSettledThreadsForSidebar(settled);
  const activeShown = withRouteThread(activeAll, activeCap);
  const settledShown = withRouteThread(settledSorted, settledVisible);
  const rows: RecentsRow[] = [
    ...pinnedSorted.map((thread) => ({ thread, section: "pinned" as const })),
    ...activeShown.map((thread) => ({ thread, section: "active" as const })),
    ...(snoozedOpen ? snoozedSorted.map((thread) => ({ thread, section: "snoozed" as const })) : []),
    ...(settledOpen ? settledShown.map((thread) => ({ thread, section: "settled" as const })) : []),
  ];
  // What is shown, the open chat included, is not hidden: Show more never offers rows already on screen.
  const settledHidden = Math.max(0, settledSorted.length - settledShown.length);
  const activeHidden = Math.max(0, activeAll.length - activeShown.length);
  const rowItems = (sections: ReadonlyArray<RecentsSection>): RecentsItem[] =>
    rows.flatMap((row, index) => (sections.includes(row.section) ? [{ kind: "row" as const, row, index }] : []));
  const items: RecentsItem[] = [
    ...rowItems(["pinned", "active"]),
    ...(activeHidden > 0 ? [{ kind: "more" as const, under: "active" as const, count: Math.min(RECENTS_PAGE_COUNT, activeHidden) }] : []),
    ...(snoozedSorted.length > 0
      ? [{ kind: "shelf" as const, shelf: "snoozed" as const, count: snoozedSorted.length, open: input.snoozedOpen }]
      : []),
    ...rowItems(["snoozed"]),
    ...(settledSorted.length > 0
      ? [{ kind: "shelf" as const, shelf: "settled" as const, count: settledSorted.length, open: input.settledOpen }]
      : []),
    ...rowItems(["settled"]),
    ...(settledOpen && settledHidden > 0
      ? [{ kind: "more" as const, under: "settled" as const, count: Math.min(RECENTS_PAGE_COUNT, settledHidden) }]
      : []),
  ];
  return {
    rows,
    items,
    snoozedCount: snoozedSorted.length,
    settledCount: settledSorted.length,
    settledHidden,
    activeHidden,
    filtering,
  };
}

export const recentsRowKey = (thread: EnvironmentThreadShell): string =>
  scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));

/** A row's key is its chat's scoped key; the other items have fixed keys. */
export function recentsItemKey(item: RecentsItem): string {
  if (item.kind === "row") return recentsRowKey(item.row.thread);
  return item.kind === "shelf" ? `shelf:${item.shelf}` : `more:${item.under}`;
}

/**
 * The row a shortcut opens: previous or next chat around the open one, or
 * mod+1..9 for the first nine rows. Null when the shortcut is neither.
 */
export function shortcutTargetRow(
  rows: ReadonlyArray<RecentsRow>,
  command: string | null,
  pathname: string,
): RecentsRow | null {
  const direction = threadTraversalDirectionFromCommand(command);
  if (direction) {
    const ids = rows.map((row) => row.thread.id as string);
    const currentId = ids.find((id) => pathname.includes(id)) ?? null;
    const adjacentId = resolveAdjacentThreadId({ threadIds: ids, currentThreadId: currentId, direction });
    return rows.find((row) => row.thread.id === adjacentId) ?? null;
  }
  const index = command ? threadJumpIndexFromCommand(command) : null;
  return index === null ? null : (rows.slice(0, 9)[index] ?? null);
}

/**
 * What a key press means to Recents, in the context the composer and model
 * picker see: with the picker open, mod+1..9 picks a model, not a chat.
 */
export function recentsShortcutCommand(
  event: ShortcutEventLike,
  keybindings: ResolvedKeybindingsConfig,
  options: { readonly platform: string; readonly terminalOpen: boolean },
): KeybindingCommand | null {
  return resolveShortcutCommand(event, keybindings, {
    platform: options.platform,
    context: {
      terminalFocus: isTerminalFocused(),
      terminalOpen: options.terminalOpen,
      modelPickerOpen: isModelPickerOpen(),
    },
  });
}

/** The item holding Recents' one tab stop: the last focused item while it is still listed, else the first. */
export function recentsTabStop(itemKeys: ReadonlyArray<string>, focusKey: string | null): string | null {
  if (focusKey !== null && itemKeys.includes(focusKey)) return focusKey;
  return itemKeys[0] ?? null;
}

export type RecentStatusKind = "approval" | "input" | "working" | "monitoring" | "failed" | "woke" | "done";

/** What a row's trailing column says, in priority order: the chat's own state, then woke, then an unseen reply. */
export function recentStatusKind(input: {
  readonly status: SidebarThreadStatus;
  readonly isWoke: boolean;
  readonly isUnread: boolean;
}): RecentStatusKind | null {
  const { status } = input;
  if (status === "approval" || status === "input" || status === "working" || status === "monitoring" || status === "failed")
    return status;
  if (input.isWoke) return "woke";
  return input.isUnread ? "done" : null;
}

/** "15h" for "15h ago", "now" for "just now". */
export function compactTime(timestamp: string | null): string {
  if (!timestamp) return "";
  const label = formatRelativeTimeLabel(timestamp);
  if (label === "just now") return "now";
  return label.endsWith(" ago") ? label.slice(0, -4) : label;
}

/**
 * What an empty Recents says. With no filter, "No chats yet" waits until the
 * shells bootstrapped and some environment answered (`threadsKnown`); before
 * that, or with every environment disconnected, the list is only not here yet.
 */
export function recentsEmptyState(input: {
  readonly empty: boolean;
  readonly filtering: boolean;
  readonly threadsKnown: boolean;
}): "loading" | "no-match" | "none" | null {
  if (!input.empty) return null;
  if (input.filtering) return "no-match";
  return input.threadsKnown ? "none" : "loading";
}
